import ErrorIcon from '@mui/icons-material/ErrorOutline';
import Button, { ButtonVariant } from 'components/common/Button/Button';

import './ErrorPlaceholder.scss';
import ELEMENT from 'common/element';
import { clearGraphQLErrorMessage } from 'common/util/string';

type Error = {
  title?: string;
  message: string;
  isCompact?: boolean;
  errorLink?: {
    text: string;
    url?: string;
    openInNewTab?: boolean;
    onClick?: () => void;
  };
};

export type ErrorPlaceholderProps = {
  error: Error;
};

/**
 *
 * Renders an error within a parent container, including an optional title and icon.
 *
 * @param props.error
 * @example usage within a component, taken from DashboardRouter:
  if (error) {
    const errorLink = {
      url: buildUrl(K.ROUTE.CONFIG_FILE, { [K.URL_PARAM.PATH]: `/dashboards/${dashboardName}`, [K.URL_PARAM.ORIGINAL_URL]: window.location.href } as UrlParams),
      text: `Edit ${dashboardName} JSON config file`,
    };
    return (
      <ErrorPlaceholder error={{
        title: 'No Dashboard Found',
        message: error.message,
        errorLink
      }}
      />
    );
  }
 */
const ErrorPlaceholder = ({ error }: ErrorPlaceholderProps): JSX.Element => {
  const errorTitle = error.title || (error.isCompact ? undefined : 'Error');
  const errorMessage = clearGraphQLErrorMessage(error.message);

  return (
    <div
      data-test-id={ELEMENT.commonErrorPlaceholder}
      className={
        error.isCompact
          ? 'ErrorPlaceholder__Container ErrorPlaceholder__Container--Compact'
          : 'ErrorPlaceholder__Container'
      }
    >
      {!error.isCompact && (
        <div className="ErrorPlaceholder__Icon">
          <ErrorIcon />
        </div>
      )}
      <div
        className={
          error.isCompact ? 'ErrorPlaceholder__Body ErrorPlaceholder__Body--Compact' : 'ErrorPlaceholder__Body'
        }
      >
        {errorTitle && <div className="ErrorPlaceholder__Title">{errorTitle}</div>}
        <div className="ErrorPlaceholder__Message">{errorMessage}</div>
        {error.errorLink ? (
          <div className="ErrorPlaceholder__Link">
            <Button
              onClick={error.errorLink.onClick}
              href={error.errorLink.url}
              openInNewTab={error.errorLink.openInNewTab}
              variant={ButtonVariant.SECONDARY}
            >
              {error.errorLink.text}
            </Button>
          </div>
        ) : undefined}
      </div>
    </div>
  );
};

export default ErrorPlaceholder;
